import { useQuery } from "@apollo/client";
import { useCallback, useMemo } from "react";

import useTypedContext from "hooks/useTypedContext";
import FlashContext from "components/FlashMessages/FlashContext";
import { makeFilterItemOptionsFromSuggestionField } from "components/Filters/helpers";
import { SearchSuggestionsFieldType } from "types/generated";
import { FilterItem, FiltersItemsOptionsMap, SortOption } from "components/Filters/types";
import useFiltersVariables from "components/Filters/useFiltersVariables";
import { useCachedFilterFields } from "components/Filters/hooks";

import { GET_CONTEXT_SUGGESTIONS } from "./gql";
import { SEARCH_CONTEXTS_SUGGESTIONS_DICTIONARY, SORT_ITEM_ORDER } from "../constants";
import { FILTER_TYPE, INITIAL_SORT_DIRECTION, INITIAL_SORT_OPTION } from "../../../constants";
import { getFilterKey } from "./helpers";

const useContextsFilters = () => {
  const { onError } = useTypedContext(FlashContext);

  const { filterVariables, filterVariablesLoaded } = useFiltersVariables({
    filterType: FILTER_TYPE,
    initialSortDirection: INITIAL_SORT_DIRECTION,
    initialSortOption: INITIAL_SORT_OPTION,
  });

  const { data, loading, refetch, called } = useQuery(GET_CONTEXT_SUGGESTIONS, {
    variables: {
      input: {
        fields: null,
        fullTextSearch: filterVariables.searchInput,
        predicates: filterVariables.predicates,
      },
    },
    skip: !filterVariablesLoaded,
    onError,
  });

  const cachedFiltersData = useCachedFilterFields(data?.searchContextsSuggestions?.fields);

  const [filters, contextsFiltersItemsOptionsMap] = useMemo<
    [FilterItem[], FiltersItemsOptionsMap]
  >(() => {
    let labelsCounter = 0;
    const filtersItemsOptionsMap: FiltersItemsOptionsMap = new Map([]);

    return [
      cachedFiltersData
        // timestamp is not a filter, it's a date range option
        .filter((field) => field.filterable && field.name !== "timestamp")
        .map((field) => {
          let key = field.name;

          if (key === "label") {
            labelsCounter += 1;
            key = `label${labelsCounter}`;
          } else {
            key = SEARCH_CONTEXTS_SUGGESTIONS_DICTIONARY[key] || key;
          }

          const options = makeFilterItemOptionsFromSuggestionField(field);

          if (options) {
            filtersItemsOptionsMap.set(field.name, options);
          }

          return {
            key,
            filterName: field.name,
            //SearchSuggestionsFieldType is only available if the field is filterable
            type: field.type as SearchSuggestionsFieldType,
          };
        }) || [],
      filtersItemsOptionsMap,
    ];
  }, [cachedFiltersData]);

  const sortOptions = useMemo((): SortOption[] => {
    return (
      cachedFiltersData
        .filter(
          (field) =>
            field.orderable && field.name !== "slug" && SORT_ITEM_ORDER.includes(field.name)
        )
        .map((field) => {
          return {
            value: field.name,
            label: getFilterKey(field.name),
          };
        })
        .sort((a, b) => SORT_ITEM_ORDER.indexOf(a.value) - SORT_ITEM_ORDER.indexOf(b.value)) || []
    );
  }, [cachedFiltersData]);

  const refreshSuggestions = useCallback(
    (fields?: string[]) => {
      if (!called) {
        refetch?.({
          input: {
            fields: fields || null,
            fullTextSearch: filterVariables.searchInput,
            predicates: filterVariables.predicates,
          },
        });
      }
    },
    [filterVariables.searchInput, filterVariables.predicates, called, refetch]
  );

  return {
    filters,
    filtersMap: contextsFiltersItemsOptionsMap,
    refetch,
    loading,
    itemsCount: data?.searchContextsSuggestions.filteredCount || 0,
    sortOptions,
    refreshSuggestions,
  };
};

export default useContextsFilters;
